/**
 * Starting crystals: what a player coalesces on the gathering page, before
 * entering, from their banked essence. Each pick is one crystal aimed at a
 * carried slot; it costs its recipe's ingredients, and it's ready the moment
 * the player enters (no coalescence time). The server checks and charges the
 * picks; the host grants them on join. Unbroken crystals are lost when the
 * run ends, like any other. See docs/empyreal-crystal-mechanics.md.
 */
import { withCrystalAddedToSlot, withCarriedCrystalsAdded, toCrystalKind, type CarriedInventorySlots } from './crystal-utils';
import { getRecipeSystem } from './recipe-system';
import type { StartingCrystalPick } from './types/crystals';
import { withEssenceAdded, createEmptyEssencePool, hasMinimumEssence, type EssencePool, type RecipeDefinition } from './types/essence';
import type { CarriedItem } from './types/game';

/** Most crystals one member can pick; more than any carried inventory holds. */
export const MAX_STARTING_CRYSTAL_PICKS = 64;

/** Reads starting-crystal picks from an untrusted payload, or undefined when it is malformed. */
export function parseStartingCrystalPicks(value: unknown): StartingCrystalPick[] | undefined {
    if (!Array.isArray(value) || value.length > MAX_STARTING_CRYSTAL_PICKS) {
        return undefined;
    }
    const picks: StartingCrystalPick[] = [];
    for (const entry of value) {
        if (typeof entry !== 'object' || entry === null) {
            return undefined;
        }
        const { recipeId, slotIndex } = entry as Partial<StartingCrystalPick>;
        if (typeof recipeId !== 'string' || recipeId.trim().length === 0
            || typeof slotIndex !== 'number' || !Number.isInteger(slotIndex) || slotIndex < 0) {
            return undefined;
        }
        picks.push({ recipeId, slotIndex });
    }
    return picks;
}

/** The standard recipe a pick names, or null when there is none. */
function findStandardRecipe(recipeId: string): RecipeDefinition | null {
    const recipe = getRecipeSystem().getRecipe(recipeId);
    return recipe !== null && recipe.isDiscoveredByDefault ? recipe : null;
}

export type StartingCrystalPickRejection = 'unknown-recipe' | 'no-room' | 'cant-afford';

/** Picks resolved against a carried inventory and a banked essence budget. */
export interface ResolvedStartingCrystalPicks {
    /** The carried inventory with every accepted crystal in its slot. */
    readonly inventory: Array<CarriedItem | undefined>;
    readonly accepted: StartingCrystalPick[];
    readonly rejected: Array<{ readonly pick: StartingCrystalPick; readonly reason: StartingCrystalPickRejection }>;
    /** What the accepted picks cost, by type. */
    readonly cost: EssencePool;
}

/**
 * Resolves picks in order: each needs a standard recipe, room in its carried
 * slot (empty, or a same-kind stack below its limit), and, with a `budget`,
 * enough banked essence left after the picks before it. A pick that fails is
 * rejected and the rest carry on. `createId` names each new stack.
 */
export function resolveStartingCrystalPicks(
    picks: ReadonlyArray<StartingCrystalPick>,
    inventory: CarriedInventorySlots,
    totalSlots: number,
    budget: Partial<EssencePool> | null,
    createId: () => string,
): ResolvedStartingCrystalPicks {
    let next: Array<CarriedItem | undefined> = Array.from({ length: Math.max(totalSlots, inventory.length) }, (_, index) => inventory[index]);
    let cost = createEmptyEssencePool();
    const available = budget === null ? null : withEssenceAdded(createEmptyEssencePool(), budget);
    const accepted: StartingCrystalPick[] = [];
    const rejected: Array<{ pick: StartingCrystalPick; reason: StartingCrystalPickRejection }> = [];

    for (const pick of picks) {
        const recipe = findStandardRecipe(pick.recipeId);
        if (recipe === null) {
            rejected.push({ pick, reason: 'unknown-recipe' });
            continue;
        }
        const nextCost = withEssenceAdded(cost, recipe.ingredients);
        if (available !== null && !hasMinimumEssence(available, nextCost)) {
            rejected.push({ pick, reason: 'cant-afford' });
            continue;
        }
        const placed = withCrystalAddedToSlot(next, totalSlots, pick.slotIndex, toCrystalKind(recipe), createId);
        if (placed === undefined) {
            rejected.push({ pick, reason: 'no-room' });
            continue;
        }
        next = placed;
        cost = nextCost;
        accepted.push(pick);
    }

    return { inventory: next, accepted, rejected, cost };
}

/**
 * Grants paid-for starting crystals into a carried inventory on join: each
 * into its chosen slot, or, if that slot has filled since it was picked,
 * wherever it fits (a same-kind stack, then an empty slot). A crystal with no
 * room anywhere, or whose recipe no longer exists, isn't granted. Returns the
 * new inventory and how many crystals were granted.
 */
export function withStartingCrystalsGranted(
    inventory: CarriedInventorySlots,
    totalSlots: number,
    picks: ReadonlyArray<StartingCrystalPick>,
    createId: () => string,
): { inventory: Array<CarriedItem | undefined>; granted: number } {
    let next: Array<CarriedItem | undefined> = Array.from({ length: Math.max(totalSlots, inventory.length) }, (_, index) => inventory[index]);
    let granted = 0;
    for (const pick of picks) {
        const recipe = findStandardRecipe(pick.recipeId);
        if (recipe === null) {
            continue;
        }
        const placed = withCrystalAddedToSlot(next, totalSlots, pick.slotIndex, toCrystalKind(recipe), createId);
        if (placed !== undefined) {
            next = placed;
            granted += 1;
            continue;
        }
        const fallback = withCarriedCrystalsAdded(next, totalSlots, { ...toCrystalKind(recipe), quantity: 1 }, createId);
        next = fallback.inventory;
        granted += fallback.added;
    }
    return { inventory: next, granted };
}

/**
 * Moves the picks aimed at carried slot `fromSlot` to `toSlot`, and those
 * aimed at `toSlot` back to `fromSlot`: how a starting stack moves (or two
 * swap) on the gathering page.
 */
export function withStartingCrystalPicksMoved(
    picks: ReadonlyArray<StartingCrystalPick>,
    fromSlot: number,
    toSlot: number,
): StartingCrystalPick[] {
    return picks.map((pick) => {
        if (pick.slotIndex === fromSlot) {
            return { ...pick, slotIndex: toSlot };
        }
        if (pick.slotIndex === toSlot) {
            return { ...pick, slotIndex: fromSlot };
        }
        return pick;
    });
}

/** The picks without the last one aimed at `slotIndex`: un-making one crystal from that stack. */
export function withoutLastStartingCrystalPick(
    picks: ReadonlyArray<StartingCrystalPick>,
    slotIndex: number,
): StartingCrystalPick[] {
    const index = picks.map((pick) => pick.slotIndex).lastIndexOf(slotIndex);
    return index < 0 ? [...picks] : [...picks.slice(0, index), ...picks.slice(index + 1)];
}
